import { Controller } from "@hotwired/stimulus";

const REVEAL_SELECTOR = [
  ".scroll-reveal",
  ".scroll-reveal--left",
  ".scroll-reveal--right",
  ".scroll-reveal--scale",
  ".scroll-reveal--fade"
].join(", ");

export default class extends Controller {
  static targets = ["item"];
  static values = {
    once: { type: Boolean, default: true },
    rootMargin: { type: String, default: "0px 0px -8% 0px" },
    threshold: { type: Number, default: 0.08 }
  };

  connect() {
    // Scope ready state to this controller root — never hide orphans page-wide
    this.element.classList.add("scroll-reveal-ready");

    if (this.prefersReducedMotion()) {
      this.revealAll();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      { rootMargin: this.rootMarginValue, threshold: this.thresholdValue }
    );

    this.observeItems();
    requestAnimationFrame(() => this.observeItems());
    // Safety: never leave marketing copy invisible if IO misses
    this.safetyTimer = window.setTimeout(() => this.revealAll(), 1200);

    this.onFrameLoad = () => this.observeItems();
    document.addEventListener("turbo:frame-load", this.onFrameLoad);
    document.addEventListener("turbo:render", this.onFrameLoad);
  }

  disconnect() {
    this.observer?.disconnect();
    if (this.safetyTimer) window.clearTimeout(this.safetyTimer);
    document.removeEventListener("turbo:frame-load", this.onFrameLoad);
    document.removeEventListener("turbo:render", this.onFrameLoad);
    this.element.classList.remove("scroll-reveal-ready");
  }

  items() {
    if (this.hasItemTarget) return this.itemTargets;
    return Array.from(this.element.querySelectorAll(REVEAL_SELECTOR));
  }

  observeItems() {
    this.items().forEach((item) => {
      if (this.onceValue && item.classList.contains("is-visible")) return;

      this.revealIfVisible(item);
      this.observer?.observe(item);
    });
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.reveal(entry.target);
    });
  }

  reveal(element) {
    const delay = element.dataset.revealDelay;
    if (delay) {
      element.style.setProperty("--reveal-delay", `${delay}ms`);
    }

    element.classList.add("is-visible");

    if (this.onceValue) {
      this.observer?.unobserve(element);
    }
  }

  revealIfVisible(item) {
    const rect = item.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const inView = rect.top < viewportHeight * 0.98 && rect.bottom > 0;

    if (inView) {
      this.reveal(item);
    }
  }

  revealAll() {
    this.items().forEach((item) => item.classList.add("is-visible"));
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}
